import clsx from 'clsx';
import React from 'react';
import { InView } from 'react-intersection-observer';

import SubscriptionBenefitsSection from '@components/pages/SubscribeTest/SubscriptionBenefitsSection';
import useDualOfferFunctionality from '@hooks/useDualOfferFunctionality';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, ImpressionElement, click, impression } from '@tracking';
import { OfferOption, SelectedOffer } from '@utils/backend-api/subscribe';

import AppSection from './AppSection';
import AudioSection from './AudioSection';
import CorporateInquiry from './CorporateInquery';
import EventsSection from './EventsSection';
import HorizontalOffer from './HorizontalOffer';
import OfferPanel from './OfferPanel';
import ProSection from './ProSection';
import TITVSection from './TITVSection';
import {
  ANNUAL_BENEFITS,
  BENEFIT_ROWS,
  PRO_BENEFITS,
  WITH_YOUR_ANNUAL_SUBSCRIPTION_TITLE,
} from './constants';

interface ContentProps {
  defaultSelectedOffer: SelectedOffer;
  latestTITVEpisodeID?: string;
  offers: OfferOption[];
  returnTo?: string;
}

const Content = ({ defaultSelectedOffer, latestTITVEpisodeID, offers, returnTo }: ContentProps) => {
  const { trackEvent } = useEventTracking();
  const { horizontalOffers, isMobile, selectedOffer, setSelectedOffer, verticalOffers } =
    useDualOfferFunctionality({ defaultSelectedOffer, offers });

  return (
    <main className="px-16 sm:px-28" data-testid="dual-subscribe-multiple-offers">
      {returnTo && (
        <div className="mb-24 text-center">
          <a
            href={returnTo}
            className="text-sm text-grayscale-dark font-suisse-intl underline"
            onClick={() =>
              trackEvent(
                click({
                  clickTarget: ClickTarget.AnchorLink,
                  clickText: '← Back to what you were reading',
                  clickLink: returnTo,
                })
              )
            }
          >
            ← Back to what you were reading
          </a>
        </div>
      )}
      <div className="md:max-w relative mx-auto w-full sm:max-w-[1040px] xl:max-w-[1130px]">
        <div className="flex max-w-full flex-wrap sm:flex-row">
          {verticalOffers.map((offer, index) => (
            <OfferPanel
              key={offer.code}
              benefits={offer.isPro ? PRO_BENEFITS : ANNUAL_BENEFITS}
              benefitsHeader={
                offer.isPro
                  ? 'Access everything in Annual plus:'
                  : 'Access exclusive tech news and more:'
              }
              benefitsHeaderBold={offer.isPro}
              presentedOffers={offers}
              offer={offer}
              index={index}
              isMobile={isMobile}
              isOfferSelected={selectedOffer.code === offer.code}
              setSelectedOffer={setSelectedOffer}
              className={clsx(
                'border border-b border-l border-r border-solid border-grayscale-beige',
                {
                  'border-r-transparent': index % 2 === 0,
                  'border-l-transparent': index % 2 !== 0,
                }
              )}
            />
          ))}
        </div>
        {horizontalOffers.length > 0 && (
          <div className="mt-64 flex flex-col items-center justify-center sm:mt-80">
            <div className="mb-16 font-suisse-screen text-headline-xxs">Other Plans</div>
            {horizontalOffers.map((offer, index) => (
              <div
                id={offer.code}
                key={offer.code}
                className={clsx(
                  'relative flex w-full flex-wrap items-center justify-between gap-16 border border-l-0 border-r-0 border-t border-solid bg-white px-16 py-24 md:w-[836px] md:gap-90 md:px-48',
                  {
                    'border-b': index === horizontalOffers.length - 1,
                    'border-b-0': index !== horizontalOffers.length - 1,
                  }
                )}
              >
                <HorizontalOffer offer={offer} />
              </div>
            ))}
          </div>
        )}
        <InView
          onChange={(inView) => {
            inView &&
              trackEvent(impression({ impressionElement: ImpressionElement.CorporateInquiry }));
          }}
          triggerOnce
        >
          <CorporateInquiry />
        </InView>
        <SubscriptionBenefitsSection
          benefits={BENEFIT_ROWS}
          title={WITH_YOUR_ANNUAL_SUBSCRIPTION_TITLE}
        />
        {latestTITVEpisodeID && (
          <InView
            onChange={(inView) => {
              inView &&
                trackEvent(impression({ impressionElement: ImpressionElement.TITVPastEvent }));
            }}
            triggerOnce
          >
            <TITVSection latestTITVEpisodeID={latestTITVEpisodeID} />
          </InView>
        )}
      </div>
      <InView
        onChange={(inView) => {
          inView && trackEvent(impression({ impressionElement: ImpressionElement.EventsSection }));
        }}
        triggerOnce
      >
        <EventsSection />
      </InView>
      <InView
        onChange={(inView) => {
          inView && trackEvent(impression({ impressionElement: ImpressionElement.AppSection }));
        }}
        triggerOnce
      >
        <AppSection />
      </InView>
      <InView
        onChange={(inView) => {
          inView && trackEvent(impression({ impressionElement: ImpressionElement.AudioSection }));
        }}
        triggerOnce
      >
        <AudioSection />
      </InView>
      <InView
        onChange={(inView) => {
          inView && trackEvent(impression({ impressionElement: ImpressionElement.ProSection }));
        }}
        triggerOnce
      >
        <ProSection />
      </InView>
    </main>
  );
};

export default Content;
